import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, catchError, combineLatest, concatMap, map, Observable, of, switchMap, tap } from 'rxjs';
import {
  AddressListRiskResDTO,
  GetHomePolicyRes,
  GetHomePolicyRisksRes,
  GetMotorPolicyRes,
  GetMotorPolicyRisksRes,
  GetPolicyRes,
  HomePolicyCreateParamDTO,
  MotorPolicyCreateParamDTO,
  PoliciesRes,
  PolicyListDTO,
  PolicyType,
  RootResOfListOfPolicyDetailDTO,
  StatusType,
  UserNationalDataResponseDTO
} from 'models/DTOs';
import { CoreApiService, RequestConfig } from '@services/core-api-service';
import { NotificationService } from '@services/notification.service';
import { API_URLS } from '../../constants/core.constants';
import { GetPoliciesByInsuredIDParamDTO, GetPolicyParamDTO } from '@shared/dtos/epic-bridge/models';
import { UserService } from 'app/services/user.service';
import { EpicBridgeService } from './epic-bridge.service';
import { Router } from '@angular/router';
import { PaymentService } from './payment.service';

@Injectable({
  providedIn: 'root'
})
export class PolicyService extends CoreApiService {
  private _notifications = inject(NotificationService);
  private _userService: UserService = inject(UserService);
  private _epicBridgeService: EpicBridgeService = inject(EpicBridgeService);
  private _paymentService: PaymentService = inject(PaymentService);
  private _router = inject(Router);

  policiesSubject: BehaviorSubject<PolicyListDTO[]> = new BehaviorSubject(null);
  policySubject: BehaviorSubject<PolicyListDTO[]> = new BehaviorSubject(null); // payments page use this observable TO REFACTOR!!!!!!!!
  expirePoliciesSubject: BehaviorSubject<PolicyListDTO[]> = new BehaviorSubject(null);
  upcomingPolicySubject: BehaviorSubject<any[]> = new BehaviorSubject(null);

  getPolicies() {
    const user = this._userService.userSubject.value;
    let obs: Observable<Partial<UserNationalDataResponseDTO>>;

    if (user?.nationalIDTypeNumber && user?.nationalIDType) {
      obs = of({
        nationalIDType: user.nationalIDType,
        nationalIDTypeNumber: user.nationalIDTypeNumber
      });
    } else if (user?.clientID) {
      obs = this._userService.getNationalData(user.clientID);
    } else {
      this._notifications.showMessage('User not properly initialized.');
      return;
    }

    obs
      .pipe(
        switchMap(data => {
          if (!data?.nationalIDType || !data?.nationalIDTypeNumber) {
            this._notifications.showMessage('User does not have national IDs.');
            return of([]);
          }

          if (!user.nationalIDType || !user.nationalIDTypeNumber) {
            this._userService.updateCachedUser(data);
          }

          const eb$ = this.getEBPolicies(data).pipe(
            map(res => {
              if (!res.success) {
                this._notifications.showMessage(`EB Error: ${res.errorMessage}`);
                return [];
              }
              return res.policies.map(
                p =>
                  ({
                    policyID: p.policy.policyId,
                    policyType: p.policy.policyType,
                    policyTitle: this.getPolicyTitle(p.policy.policyType),
                    policyStatus: p.policy.status,
                    policyNumber: p.policy.policyNumber,
                    coverageType: p.policy.typeOfCover,
                    coverageDateFrom: p.policy.startDate,
                    coverageDateTo: p.policy.endDate,
                    amount: p.policy.annualPremium,
                    nextPayment: p.policy.dateForRenewal
                  }) as PolicyListDTO
              );
            }),
            catchError(err => {
              this._notifications.showMessage(`EB exception: ${err.message || err}`);
              return of([]);
            })
          );

          const hth$ = of([]);
          // const hth$ = user.memberID
          //   ? this.getHealthPolicy(user.memberID).pipe(
          //       map(res => {
          //         if (!res.success) {
          //           this._notifications.showMessage(`HTH Error: ${res.errorMessage}`);
          //           return [];
          //         }
          //         return res.data.map(
          //           p =>
          //             ({
          //               policyID: '1', //Update after API is updated
          //               policyType: PolicyType.Health,
          //               policyTitle: this.getPolicyTitle(PolicyType.Health),
          //               policyStatus: p.status,
          //               policyNumber: 0, //Update after API is updated?
          //               coverageType: p.coverageType,
          //               coverageDateFrom: p.effectiveDate, //Update after API is updated?
          //               coverageDateTo: p.effectiveDate, //Update after API is updated
          //               amount: p.billAmount,
          //               nextPayment: ''
          //             }) as PolicyListDTO
          //         );
          //       }),
          //       catchError(err => {
          //         this._notifications.showMessage(`HTH exception: ${err.message || err}`);
          //         return of([]);
          //       })
          //     )
          //   : of([]);

          return combineLatest([eb$, hth$]).pipe(map(([ebPolicies, hthPolicies]) => [...ebPolicies, ...hthPolicies]));
        })
      )
      .subscribe(policies => {
        this.policiesSubject.next(policies);
        const expiring = policies.filter(p => p.policyStatus.replace(' ', '') === StatusType[StatusType.ExpiringSoon]);
        this.expirePoliciesSubject.next(expiring);
      });
  }

  // getPolicyList(clientID: string) {
  //   this.policySubject.next(null);
  //   this.upcomingPolicySubject.next(null);

  //   this.get<PolicyListDTO[]>(`policies/${clientID}`).subscribe(response => {
  //     this.policySubject.next(response);

  //     const nullPolicy: PolicyListDTO = {
  //       policyID: '',
  //       policyType: PolicyType.Home,
  //       policyTitle: '',
  //       policyNumber: 0,
  //       policyStatus: '',
  //       coverageDateFrom: undefined,
  //       coverageDateTo: undefined,
  //       coverageType: '',
  //       amount: 0,
  //       nextPayment: ''
  //     };

  //     const healthPolicy = response.find(
  //       item => item.policyType === PolicyType.Health && item.policyStatus === StatusType[StatusType.Active]
  //     ) ?? {
  //       ...nullPolicy,
  //       policyType: PolicyType.Health
  //     };
  //     healthPolicy.policyTitle = 'Health Policy';

  //     const motorPolicy = response.find(
  //       item =>
  //         item.policyType === PolicyType.Motor && StatusType[item.policyStatus.replace(' ', '')] === StatusType.InForce
  //     ) ?? {
  //       ...nullPolicy,
  //       policyType: PolicyType.Motor
  //     };
  //     motorPolicy.policyTitle = 'Motor Policy';

  //     const homePolicy = response.find(
  //       item =>
  //         item.policyType === PolicyType.Home && StatusType[item.policyStatus.replace(' ', '')] === StatusType.InForce
  //     ) ?? {
  //       ...nullPolicy,
  //       policyType: PolicyType.Home
  //     };
  //     homePolicy.policyTitle = 'Home Policy';

  //     const items = response.filter(item => item.policyStatus === StatusType[StatusType.ExpiringSoon]);
  //     this.expirePoliciesSubject.next(items);
  //     this.upcomingPolicySubject.next([healthPolicy, motorPolicy, homePolicy]);
  //   });
  // }

  getEBPolicies(data: Partial<UserNationalDataResponseDTO>) {
    const params: GetPoliciesByInsuredIDParamDTO = {
      nationalId: data.nationalIDTypeNumber,
      nationalIdType: this._epicBridgeService.getNationalIdType(data.nationalIDType),
      activeOnly: false
    };

    return this.get<PoliciesRes>(API_URLS.ebPolicies, { params: { ...params } });
  }

  getEBPolicy(
    policyId: string,
    policyType: PolicyType = null
  ): Observable<GetPolicyRes | GetMotorPolicyRes | GetHomePolicyRes> {
    const params: GetPolicyParamDTO = {
      policyId: policyId,
      includeDeleted: false,
      includeReinsurance: false,
      returnMainLocationOnly: false,
      returnRenewalRates: true
    };

    switch (policyType) {
      case PolicyType.Motor:
        return this.get<GetMotorPolicyRes>(API_URLS.epicbridge + '/' + API_URLS.getMotorPolicy, {
          params: { ...params }
        });
      case PolicyType.Home:
        return this.get<GetHomePolicyRes>(API_URLS.epicbridge + '/' + API_URLS.getHomePolicy, {
          params: { ...params }
        });
      default:
        return this.get<GetPolicyRes>(API_URLS.epicbridge + '/' + API_URLS.getPolicy, { params: { ...params } });
    }
  }

  getEBMotorPolicyRisks(policyId: string) {
    const params = {
      policyId: policyId
    };

    return this.get<GetMotorPolicyRisksRes>(API_URLS.epicbridge + '/' + API_URLS.motorPolicyRisks, { params: params });
  }

  getEBHomePolicyRisks(policyId: string) {
    const params = {
      policyId: policyId
    };

    return this.get<GetHomePolicyRisksRes>(API_URLS.epicbridge + '/' + API_URLS.homePolicyRisks, { params: params });
  }

  getEBRiskLocation(data: Partial<UserNationalDataResponseDTO>) {
    const params: GetPoliciesByInsuredIDParamDTO = {
      nationalId: data.nationalIDTypeNumber,
      nationalIdType: data.nationalIDType,
      activeOnly: true
    };

    return this.get<AddressListRiskResDTO>(API_URLS.ebRiskLoactions, { params: { ...params } });
  }

  getHealthPolicy(memberid: string) {
    const params = {
      memberId: memberid
    };

    return this.get<RootResOfListOfPolicyDetailDTO>(API_URLS.getHealthPolicy, { params: params });
  }

  getPolicyTitle(policyType: PolicyType) {
    switch (policyType) {
      case PolicyType.Home: {
        return 'Home Policy';
      }
      case PolicyType.Motor: {
        return 'Motor Policy';
      }
      case PolicyType.Health: {
        return 'Health Policy';
      }
      default:
        return '';
    }
  }

  getIcon(policyType: PolicyType) {
    switch (policyType) {
      case PolicyType.Home: {
        return 'home';
      }
      case PolicyType.Motor: {
        return 'directions_car';
      }
      case PolicyType.Health: {
        return 'cardiology';
      }
    }
  }

  exportPDF(fileName: string) {
    const requestConfig: RequestConfig = {
      responseType: 'blob'
    };

    this.get(API_URLS.exportPDF + '/' + fileName, requestConfig).subscribe(response => {
      const url = window.URL.createObjectURL(response);
      window.open(url);
    });
  }

  processPaymentAndActivation(clientID: string, amount: number, latestQuoteNumber: number, policyType: string) {
    const payItem$ = this._paymentService.payItem(clientID, amount);
    const activatePolicy$ = this.activatePolicy(latestQuoteNumber, policyType);

    return payItem$.pipe(
      concatMap((response: any) => {
        if (response?.success) {
          return activatePolicy$;
        } else {
          return of(null);
        }
      })
    );
  }

  activatePolicy(quotationNumber: number, policyType: string) {
    const params = {
      quoteNumber: quotationNumber,
      policyType: policyType
    };

    return this.post(API_URLS.ebQuoteToPolicy, params);
    //.pipe(
    // tap(response => {
    //   if (response.success)
    //     this._router.navigate(['success-message', { type: 'policyCreated', id: response.policyNumber }]);
    //   else {
    //     console.log(response.errorMessage);
    //     this._notifications.showMessage('Error in creating the policy. Please contact our customer support.');
    //   }
    // })
    //);
  }

  createMotorPolicy(motorPolicy: MotorPolicyCreateParamDTO) {
    return this.post(API_URLS.ebMotorPolicyCreate, motorPolicy);
  }

  createHomePolicy(homePolicy: HomePolicyCreateParamDTO) {
    return this.post(API_URLS.ebHomePolicyCreate, homePolicy);
  }
}
